import { requireById } from './dom'
import { hideTip, showTip, showToast } from './admin-ui'

/** Weekly revenue, winter season ramp. Labels are week-endings. */
const WEEKS: ReadonlyArray<{ label: string; revenue: number }> = [
  { label: '21 Jun', revenue: 6240 },
  { label: '28 Jun', revenue: 7130 },
  { label: '5 Jul', revenue: 8420 },
  { label: '12 Jul', revenue: 9860 },
  { label: '19 Jul', revenue: 11890 },
  { label: '26 Jul', revenue: 13380 },
  { label: '2 Aug', revenue: 14760 },
  { label: '9 Aug', revenue: 16110 },
]

/** Bookings by channel this month. Direct channels pay no commission. */
const CHANNELS: ReadonlyArray<{ label: string; count: number; direct: boolean }> = [
  { label: 'Your website', count: 32, direct: true },
  { label: 'Viator', count: 30, direct: false },
  { label: 'Phone (AI)', count: 15, direct: true },
  { label: 'GetYourGuide', count: 12, direct: false },
  { label: 'Email (AI)', count: 6, direct: true },
]

/* Chart geometry for the weekly-revenue columns. */
const W = 560
const H = 220
const PAD_LEFT = 40
const PAD_TOP = 16
const PAD_BOTTOM = 28
const BAR_WIDTH = 22
const SCALE_MAX = 17000
const TICKS = [0, 5000, 10000, 15000]

const money = (n: number): string => `$${n.toLocaleString('en-AU')}`
const moneyShort = (n: number): string => `$${(n / 1000).toFixed(1)}k`

function sparkline(): string {
  const points = WEEKS.map((w, i) => {
    const x = 4 + (i * 88) / (WEEKS.length - 1)
    const y = 24 - ((w.revenue - 6000) / 10500) * 20
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const [endX = 0, endY = 0] = (points[points.length - 1] ?? '').split(',').map(Number)
  return (
    `<svg class="spark" viewBox="0 0 96 28" aria-hidden="true">` +
    `<polyline points="${points.join(' ')}" fill="none" stroke="var(--dim)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<circle cx="${endX}" cy="${endY}" r="3.5" fill="var(--accent)" stroke="#fff" stroke-width="2"/>` +
    `</svg>`
  )
}

function revenueColumns(): string {
  const plotHeight = H - PAD_TOP - PAD_BOTTOM
  const baseline = H - PAD_BOTTOM
  const band = (W - PAD_LEFT - 8) / WEEKS.length

  const grid = TICKS.map((t) => {
    const y = baseline - (t / SCALE_MAX) * plotHeight
    const line = `<line x1="${PAD_LEFT}" y1="${y.toFixed(1)}" x2="${W}" y2="${y.toFixed(1)}" class="${t === 0 ? 'axis' : 'grid'}"/>`
    const tick = `<text x="${PAD_LEFT - 6}" y="${(y + 3.5).toFixed(1)}" class="ytick">${t / 1000}k</text>`
    return line + tick
  }).join('')

  const bars = WEEKS.map((w, i) => {
    const height = (w.revenue / SCALE_MAX) * plotHeight
    const x = PAD_LEFT + i * band + (band - BAR_WIDTH) / 2
    const y = baseline - height
    // Rounded 4px at the data end, square at the baseline.
    const path =
      `M${x.toFixed(1)},${(y + 4).toFixed(1)} a4,4 0 0 1 4,-4 h${BAR_WIDTH - 8} a4,4 0 0 1 4,4 ` +
      `v${(height - 4).toFixed(1)} h${-BAR_WIDTH} Z`
    const last = i === WEEKS.length - 1
    const cap = last
      ? `<text x="${(x + BAR_WIDTH / 2).toFixed(1)}" y="${(y - 7).toFixed(1)}" class="cap">${moneyShort(w.revenue)}</text>`
      : ''
    const xlabel = `<text x="${(x + BAR_WIDTH / 2).toFixed(1)}" y="${H - 8}" class="xtick">${w.label}</text>`
    return (
      `<path d="${path}" class="col" tabindex="0" role="img" ` +
      `aria-label="Week ending ${w.label}: ${money(w.revenue)}" data-tip="w/e ${w.label} — ${money(w.revenue)}"/>` +
      cap +
      xlabel
    )
  }).join('')

  return `<svg class="cols" viewBox="0 0 ${W} ${H}" role="group" aria-label="Revenue by week, last 8 weeks">${grid}${bars}</svg>`
}

function channelBars(): string {
  const max = Math.max(...CHANNELS.map((c) => c.count))
  return CHANNELS.map((c) => {
    const width = ((c.count / max) * 100).toFixed(1)
    const kind = c.direct ? 'direct' : 'ota'
    const commission = c.direct ? 'no commission' : '20–30% commission'
    return (
      `<div class="hrow" tabindex="0" data-tip="${c.label} — ${c.count} bookings · ${commission}">` +
      `<span class="hlabel">${c.label}</span>` +
      `<span class="htrack"><i class="hbar ${kind}" style="width:${width}%"></i></span>` +
      `<b class="hval">${c.count}</b></div>`
    )
  }).join('')
}

/** Visually-hidden table twin, so every chart value is readable without the chart. */
function srTable(caption: string, rows: ReadonlyArray<readonly [string, string]>): string {
  return (
    `<table class="sr-only"><caption>${caption}</caption>` +
    `<tbody>${rows.map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('')}</tbody></table>`
  )
}

/**
 * The performance view: a KPI row, the season-ramp revenue chart, the
 * direct-vs-OTA channel chart, and the plain-English reporting teaser.
 * Static sample data — the point is what the operator gets to see at all.
 */
export function initPerformance(): void {
  const body = requireById('perfbody')

  body.innerHTML = `
    <div class="perf-tiles">
      <div class="tile">
        <label>Revenue — August so far</label>
        <div class="tv">$21,940</div>
        <div class="td up">↑ 22% vs last August</div>
        ${sparkline()}
      </div>
      <div class="tile">
        <label>Seats filled</label>
        <div class="tv">248</div>
        <div class="td up">↑ 81% of capacity</div>
        <div class="ts">306 seats offered across 18 departures</div>
      </div>
      <div class="tile">
        <label>Direct bookings</label>
        <div class="tv">56%</div>
        <div class="td up">↑ from 31% last August</div>
        <div class="ts">$2,140 in OTA commission avoided</div>
      </div>
      <div class="tile">
        <label>Booked after hours</label>
        <div class="tv">$4,970</div>
        <div class="ts">23 bookings taken while nobody was at the desk — nights, dawns and one Sunday 9:38 pm</div>
      </div>
    </div>
    <div class="perf-charts">
      <div class="chart-card">
        <div class="cc-head"><b>Revenue by week</b><span>the winter season ramp</span></div>
        ${revenueColumns()}
        ${srTable('Revenue by week', WEEKS.map((w) => [`Week ending ${w.label}`, money(w.revenue)] as const))}
      </div>
      <div class="chart-card">
        <div class="cc-head"><b>Bookings by channel</b><span>August</span></div>
        <div class="cc-legend"><span><i class="key direct"></i>Direct — no commission</span><span><i class="key ota"></i>OTA — 20–30% commission</span></div>
        <div class="hbars">${channelBars()}</div>
        ${srTable('Bookings by channel, August', CHANNELS.map((c) => [c.label, `${c.count} bookings`] as const))}
      </div>
    </div>
    <div class="perf-row">
      <div class="perf-insight">
        <label class="mono">Toura noticed</label>
        <p>June’s Viator guests are coming back direct for the school holidays — 11 rebookings this month, $0 commission. Direct share is up 9 points on July.</p>
      </div>
      <button type="button" class="perf-ask" id="perfask">
        <span class="mono">Ask your business anything…</span>
        “How’s Wednesday’s 9 am tracking against last winter?”
      </button>
    </div>`

  // Tooltips enhance the marks; values are all directly labelled too.
  body.querySelectorAll<HTMLElement | SVGElement>('[data-tip]').forEach((mark) => {
    const tip = mark.getAttribute('data-tip') ?? ''
    const show = (): void => showTip(mark, tip)
    mark.addEventListener('mouseenter', show)
    mark.addEventListener('focus', show)
    mark.addEventListener('mouseleave', hideTip)
    mark.addEventListener('blur', hideTip)
  })

  requireById('perfask').addEventListener('click', () => {
    showToast('Plain-English reporting ships with the product — ask it anything, it has all the numbers.')
  })
}
